/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { DialogActions } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useForm } from "cms/forms/context";
import { ButtonLargeOutlinedGrey } from "components/page-components/buttons/button-large-outlined-grey";
import { ButtonLargePrimary } from "components/page-components/buttons/button-large-primary";
import { CreateDialog } from "components/page-components/dialogs/create-dialog";
import { setMode } from "data/store/reducers/form";
import { columnsDuplicateConfig, dataDuplicateGridProps } from "./config";
import { transformRows } from "./transform";
import { isFalse, isTrue } from "utils/empty-exists";
import { useFormSearch } from "./use-form-search";
import { SearchToolBar } from "./search-tool-bar";
import { SVGIcons } from "cms/icons/svg-icons";

const ExistingFormsDialog = (props) => {

    const form_id = props?.form_id;
    const onChangeSelectedForm = props?.onChangeSelectedForm;
    const open = props?.open;
    const setOpen = props?.setOpen;
    const setCreateFormFlowDialogOpen = props?.setCreateFormFlowDialogOpen;
    const modal = props?.modal;
        
    const dispatch = useDispatch();

    const {
        duplicateSchemaForm,
        loadCurrentSchemaForm
    } = useForm();

    const { t } = useTranslation();
    const navigate = useNavigate();

    const { loading, forms, searchForms } = useFormSearch();

    const [sortModel, setSortModel] = useState(
        [{ field: "content_updated_at", sort: "desc" }]
    );
    const [selectedGuid, setSelectedGuid] = useState();

    // Data Columns
    const columns = [...columnsDuplicateConfig];

    const onSelectedRow = (guids) => {
        const firstGuid = guids[0];
        setSelectedGuid(firstGuid);
        if (firstGuid && typeof onChangeSelectedForm === "function") {
            onChangeSelectedForm(firstGuid);
        }
    };
    const onSetSortModel = (sortable) => {
        setSortModel(sortable);
    };

    const handleSelectForm = async () => {
        await loadCurrentSchemaForm(form_id);
        setOpen(false);
        setCreateFormFlowDialogOpen(true);
        dispatch(setMode("edit"));
    };

    async function onClickDuplicate () {
        const form = await duplicateSchemaForm(selectedGuid);
        setOpen(false);
        navigate(`/forms/${form.type}/build/${form.guid}`);
    }

    return (
        <CreateDialog
            open={open}
            onClose={() => setOpen(false)}
            title={modal === "form" ? t("Forms.createForm") : t("Forms.findForm")}
            subTitle={modal === "form" ? t("Forms.duplicateFormRemarks") : t("Forms.selectAnExistingForm")}
            size='medium'
        >
            <DataGrid
                className='form-data-grid duplicate'
                columns={columns}
                rows={transformRows(forms)}
                rowCount={forms.length}
                components={{ Toolbar: SearchToolBar }}
                componentsProps={{ toolbar: { onChange: searchForms } }}
                loading={loading}
                sortModel={sortModel}
                onSortModelChange={(model) => onSetSortModel(model)}
                onSelectionModelChange={(ids) => onSelectedRow(ids)}
                {...dataDuplicateGridProps}
            />
            <DialogActions
                className={`existing-forms-dialog ${modal === "form" ? "spaceBetween" : "flexEnd"}`}
            >
                {isTrue(modal === "form") &&
                    <>
                        <ButtonLargeOutlinedGrey
                            startIcon={<SVGIcons.common.Back />}
                            label={t("commons.back")}
                            onClick={() => setOpen(false)}
                        />
                        <ButtonLargePrimary
                            disabled={!selectedGuid}
                            label={t("Forms.duplicateForm")}
                            onClick={onClickDuplicate}
                        />
                    </>
                }
                {isFalse(modal === "form") &&
                    <ButtonLargePrimary
                        startIcon={<SVGIcons.common.Approve />}
                        disabled={!form_id}
                        label={t("Forms.useForm")}
                        onClick={handleSelectForm}
                    />
                }
            </DialogActions>
        </CreateDialog>
    );
};

export { ExistingFormsDialog };
